import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Grid3X3, Search, Ship, Package, Plane, Truck, Bot, Mail, Cpu, 
  Globe, MapPin, Calculator, User, Calendar, History, FileText, 
  Container, Anchor, ExternalLink, Play, Link2, CheckCircle2, Radio
} from 'lucide-react';

const VisionBoard = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [activeTab, setActiveTab] = useState('api-connectivity');

  // Vision Board modules data
  const visionModules = [
    {
      id: 'rate-matrix',
      title: 'Rate Matrix',
      description: 'Get instant quotes with single and multi-search capabilities',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop',
      icon: Search,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20'
    },
    {
      id: 'cargo-rates',
      title: 'CargoRates.ai',
      description: 'AI-powered rate intelligence and optimization',
      image: null,
      isLogo: true,
      icon: Cpu,
      color: 'from-indigo-500 to-purple-500',
      bgColor: 'bg-gradient-to-br from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-900'
    },
    {
      id: 'fcl-export-import',
      title: 'FCL Export/Import',
      description: 'Full container load services for global trade',
      image: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=400&h=300&fit=crop',
      icon: Ship,
      color: 'from-orange-500 to-amber-500',
      bgColor: 'bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20'
    },
    {
      id: 'lcl-consolidation',
      title: 'LCL/Consolidation',
      description: 'Less than container load and cargo consolidation',
      image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=400&h=300&fit=crop',
      icon: Package,
      color: 'from-emerald-500 to-teal-500',
      bgColor: 'bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20'
    },
    {
      id: 'air-export-import',
      title: 'Air Export/Import',
      description: 'Fast air freight for time-sensitive cargo',
      image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&h=300&fit=crop',
      icon: Plane,
      color: 'from-sky-500 to-blue-500',
      bgColor: 'bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20'
    },
    {
      id: 'ro-ro-module',
      title: 'RO-RO Module',
      description: 'Roll-on/Roll-off vehicle shipping solutions',
      image: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400&h=300&fit=crop',
      icon: Truck,
      color: 'from-violet-500 to-purple-500',
      bgColor: 'bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20'
    },
    {
      id: 'domestic-module',
      title: 'Domestic Module',
      description: 'Nationwide transportation and logistics',
      image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=400&h=300&fit=crop',
      icon: Truck,
      color: 'from-rose-500 to-pink-500',
      bgColor: 'bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-900/20 dark:to-pink-900/20'
    },
    {
      id: 'crm',
      title: 'CRM',
      description: 'Customer relationship management system',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop',
      icon: User,
      color: 'from-cyan-500 to-teal-500',
      bgColor: 'bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20'
    },
    {
      id: 'wms-po-management',
      title: 'WMS/PO Management',
      description: 'Warehouse and purchase order management',
      image: 'https://images.unsplash.com/photo-1553413077-190dd305871c?w=400&h=300&fit=crop',
      icon: Package,
      color: 'from-amber-500 to-yellow-500',
      bgColor: 'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20'
    },
    {
      id: 'robotics',
      title: 'Robotics',
      description: 'Automated process robotics and AI automation',
      image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=400&h=300&fit=crop',
      icon: Bot,
      color: 'from-blue-600 to-indigo-600',
      bgColor: 'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20'
    },
    {
      id: 'email-response-bot',
      title: 'Email Response Bot',
      description: 'AI-powered email automation with video support',
      image: 'https://images.unsplash.com/photo-1596526131083-e8c633c948d2?w=400&h=300&fit=crop',
      icon: Mail,
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20',
      hasVideo: true
    },
    {
      id: 'info-x-ai',
      title: 'Info-X AI',
      description: 'Advanced AI analytics and insights engine',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=300&fit=crop',
      icon: Cpu,
      color: 'from-purple-600 to-violet-600',
      bgColor: 'bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20'
    },
    {
      id: 'api-grid',
      title: 'API Grid',
      description: 'Application Programming Interface integrations',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=300&fit=crop',
      icon: Globe,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20'
    },
    {
      id: 'container-tracking',
      title: 'Container Tracking',
      description: 'Real-time container and shipment tracking',
      image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=400&h=300&fit=crop',
      icon: MapPin,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20'
    },
    {
      id: 'accounting',
      title: 'Accounting',
      description: 'Financial management and invoicing',
      image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop',
      icon: Calculator,
      color: 'from-slate-500 to-gray-500',
      bgColor: 'bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-900/20 dark:to-gray-900/20'
    },
    {
      id: 'digital-customer-login',
      title: 'Digital Customer Login',
      description: 'Self-service customer portal access',
      image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=400&h=300&fit=crop',
      icon: User,
      color: 'from-indigo-500 to-blue-500',
      bgColor: 'bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20'
    },
    {
      id: 'vessel-schedule',
      title: 'Vessel Schedule',
      description: 'Ship schedules and departure tracking',
      image: 'https://images.unsplash.com/photo-1605745341112-85968b19335b?w=400&h=300&fit=crop',
      icon: Calendar,
      color: 'from-teal-500 to-cyan-500',
      bgColor: 'bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20'
    },
    {
      id: 'api-edi-center',
      title: 'API/EDI Center',
      description: 'Electronic data interchange hub',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&h=300&fit=crop',
      icon: Globe,
      color: 'from-blue-600 to-purple-600',
      bgColor: 'bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20'
    },
    {
      id: 'customer-rate-history',
      title: 'Customer Rate Search History',
      description: 'Historical rate searches and analytics',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop',
      icon: History,
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20'
    },
    {
      id: 'vgm-module',
      title: 'VGM Module',
      description: 'Verified Gross Mass compliance - SOLAS',
      image: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=400&h=300&fit=crop',
      icon: Container,
      color: 'from-red-500 to-orange-500',
      bgColor: 'bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20'
    },
    {
      id: 'terminal-tracking',
      title: 'Terminal Tracking',
      description: 'Port terminal operations monitoring',
      image: 'https://images.unsplash.com/photo-1511578194003-00c80e42dc9b?w=400&h=300&fit=crop',
      icon: Anchor,
      color: 'from-slate-600 to-slate-500',
      bgColor: 'bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-900/20 dark:to-gray-900/20'
    },
    {
      id: 'fmc',
      title: 'FMC',
      description: 'Federal Maritime Commission compliance',
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=400&h=300&fit=crop',
      icon: FileText,
      color: 'from-blue-700 to-blue-600',
      bgColor: 'bg-gradient-to-br from-blue-50 to-slate-50 dark:from-blue-900/20 dark:to-slate-900/20'
    },
    {
      id: 'drayage-module',
      title: 'Drayage Module',
      description: 'Short-haul container transportation',
      image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=400&h=300&fit=crop',
      icon: Truck,
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20'
    }
  ];

  // API Connectivity modules data
  const apiModules = [
    {
      id: 'cargo-smart',
      title: 'EDI with Cargo Smart',
      logo: 'CargoSmart',
      description: 'EDI Partner for Online Booking, SI, AMS, ISF, AFR & AES filing.',
      isActive: true,
      color: 'border-blue-500'
    },
    {
      id: 'ace-aes',
      title: 'ACE/AES EDI',
      logo: 'ACE',
      logoSubtext: 'For AES EDI',
      description: 'Direct EDI with ACE for filing AES in both Web and EDI Upload feature.',
      isActive: true,
      color: 'border-green-500'
    },
    {
      id: 'inttra-booking',
      title: 'INTTRA BOOKING',
      logo: 'INTTRA',
      description: 'Inbuilt function for customer to request booking with INTTRA from Info-X.',
      isActive: true,
      color: 'border-blue-600'
    },
    {
      id: 'evgm',
      title: 'eVGM',
      logo: null,
      logoImage: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=150&h=150&fit=crop',
      description: 'eVGM Filing feature through INTTRA',
      isActive: true,
      color: 'border-orange-500'
    },
    {
      id: 'inttra-si',
      title: 'INTTRA SI SUBMISSION',
      logo: 'INTTRA',
      description: 'Coordinate the Ocean Import & Export Operations',
      isActive: true,
      color: 'border-blue-600'
    },
    {
      id: 'vessel-schedule-edi',
      title: 'VESSEL SCHEDULE',
      logo: null,
      logoImage: 'https://images.unsplash.com/photo-1605745341112-85968b19335b?w=150&h=150&fit=crop',
      description: 'Vessel Schedules EDI with INTTRA',
      isActive: true,
      color: 'border-teal-500'
    },
    {
      id: 'ocean-tracking',
      title: 'OCEAN TRACKING STATUS',
      logo: 'INTTRA',
      description: 'EDI with INTTRA for all carrier ocean status',
      isActive: true,
      color: 'border-blue-600'
    },
    {
      id: 'win-webconnect',
      title: 'WIN WEBCONNECT',
      logo: 'WIN',
      logoSubtext: 'Worldwide Information Network',
      description: 'Ocean status EDI feed with Win Web Connect',
      isActive: true,
      color: 'border-purple-500'
    },
    {
      id: 'quickbooks',
      title: 'QUICKBOOKS ONLINE',
      logo: 'QB',
      logoStyle: 'quickbooks',
      description: 'EDI connectivity with online Quickbooks for transferring A/R and A/P',
      isActive: true,
      color: 'border-green-600'
    },
    {
      id: 'bank-of-america',
      title: 'BANK OF AMERICA',
      logo: 'Bank of America',
      logoStyle: 'boa',
      description: 'Connection with BOA to link payment gateway with Bank of America',
      isActive: true,
      color: 'border-red-600'
    },
    {
      id: 'intermodal-byc',
      title: 'INTERMODAL RATE EDI(BYC)',
      logo: 'BYC',
      logoSubtext: 'BOOKYOURCARGO.COM',
      description: 'Direct EDI with Bookyourcargo to provide INTERMODAL rate for esteemed customer',
      isActive: true,
      color: 'border-orange-500'
    },
    {
      id: 'intermodal-ims',
      title: 'INTERMODAL RATE EDI(IMS)',
      logo: 'IMS',
      description: 'EDI connection with IMS for our esteemed customer',
      isActive: false,
      color: 'border-blue-500'
    },
    {
      id: 'intermodal-draymaster',
      title: 'INTERMODAL RATE EDI(DRAYMASTER)',
      logo: 'DRAYMASTER',
      logoSubtext: 'ENTERPRISE',
      description: 'Getting INTERMODAL rates through EDI with Door and its nearest terminal',
      isActive: false,
      color: 'border-indigo-500'
    },
    {
      id: 'ltl-project44',
      title: 'LTL RATE EDI(PROJECT-44)',
      logo: 'project44',
      description: 'Online EDI connection for our esteemed customer',
      isActive: false,
      color: 'border-blue-400'
    },
    {
      id: 'ltl-shiprite',
      title: 'LTL RATE EDI(SHIPRITE)',
      logo: 'ShipRite.com',
      description: 'LTL Rate feed connected from Shiprite for our valued customer',
      isActive: true,
      color: 'border-green-500'
    },
    {
      id: 'air-freight-7l',
      title: 'AIR FREIGHT EDI(7L)',
      logo: '7L Freight',
      description: 'EDI connection with 7L Freight for all International AIR Cargo Rate',
      isActive: false,
      color: 'border-sky-500'
    },
    {
      id: 'rate-quote-xenon',
      title: 'RATE QUOTE EDI(XENON)',
      logo: 'XENON-FS',
      description: 'In process to establish EDI feed for Rate Quote with Xenon',
      isActive: false,
      color: 'border-gray-500'
    },
    {
      id: 'profit-tool',
      title: 'PROFIT TOOL(TERMINAL TRACKING)',
      logo: 'Profit Tools',
      logoSubtext: 'FOR TRUCKING',
      description: 'EDI feed for Terminal tracking status',
      isActive: false,
      color: 'border-emerald-500'
    },
    {
      id: 'dat-carrier',
      title: 'DAT Carrier Watch',
      logo: 'DAT',
      description: 'DAT Solutions, originally known as Dial-a-Truck, is a US-based freight exchange service',
      isActive: true,
      color: 'border-green-600'
    },
    {
      id: 'ams-isf',
      title: 'AMS/ISF 10+2',
      logo: 'ARTEMUS',
      logoSubtext: 'TRANSPORTATION SOLUTIONS',
      description: 'United States Customs and Border Protection (CBP) Importer Security Filing (ISF)',
      isActive: true,
      color: 'border-blue-700'
    },
    {
      id: 'sage',
      title: 'SAGE',
      logo: 'sage',
      logoStyle: 'sage',
      description: 'SAGE products for small and medium-sized businesses accounting applications',
      isActive: false,
      color: 'border-green-500'
    },
    {
      id: 'currency-edi',
      title: 'CURRENCY EDI',
      logo: 'XE',
      logoStyle: 'xe',
      description: 'Currency EDI with XE.com',
      isActive: false,
      color: 'border-blue-500'
    },
    // Carrier Partners
    {
      id: 'a-duie-pyle',
      title: 'A. Duie Pyle',
      logo: 'A.Duie PYLE',
      logoStyle: 'pyle',
      logoSubtext: 'PYLE PEOPLE DELIVER',
      description: 'Regional LTL freight carrier providing supply chain solutions across Northeast US',
      isActive: true,
      color: 'border-orange-500',
      isCarrier: true
    },
    {
      id: 'pace-motor',
      title: 'PACE Motor Lines',
      logo: 'PACE',
      logoStyle: 'pace',
      logoSubtext: 'motor lines',
      description: 'Regional trucking company specializing in LTL freight services',
      isActive: true,
      color: 'border-amber-700',
      isCarrier: true
    },
    {
      id: 'old-dominion',
      title: 'Old Dominion Freight Line',
      logo: 'OD',
      logoStyle: 'od',
      description: 'Leading less-than-truckload (LTL) carrier providing regional, inter-regional and national services',
      isActive: true,
      color: 'border-green-600',
      isCarrier: true
    },
    {
      id: 'forward-air',
      title: 'Forward Air',
      logo: 'Forward Air',
      logoStyle: 'forwardair',
      logoSubtext: 'TRANSPORTATION SERVICES',
      description: 'Asset-light freight and logistics company providing LTL, truckload, intermodal and logistics services',
      isActive: true,
      color: 'border-red-500',
      isCarrier: true
    },
    {
      id: 'tax-air',
      title: 'Tax-Air',
      logo: 'Tax-Air',
      logoStyle: 'taxair',
      description: 'Express freight and logistics solutions with expedited delivery services',
      isActive: true,
      color: 'border-blue-600',
      isCarrier: true
    },
    {
      id: 'saia',
      title: 'SAIA LTL Freight',
      logo: 'SAIA',
      logoStyle: 'saia',
      description: 'National LTL carrier offering regional and interregional shipping services',
      isActive: true,
      color: 'border-red-600',
      isCarrier: true
    },
    {
      id: 'arcbest',
      title: 'ArcBest',
      logo: 'ArcBest',
      logoStyle: 'arcbest',
      description: 'Integrated logistics company providing freight transportation and supply chain solutions',
      isActive: true,
      color: 'border-blue-800',
      isCarrier: true
    },
    {
      id: 'gkt-logistics',
      title: 'GKT Logistics',
      logo: 'GKT',
      logoStyle: 'gkt',
      logoSubtext: 'LOGISTICS',
      description: 'Full-service logistics provider offering warehousing, fulfillment and transportation',
      isActive: true,
      color: 'border-blue-700',
      isCarrier: true
    },
    {
      id: 'moran',
      title: 'MORAN Transportation',
      logo: 'MORAN',
      logoStyle: 'moran',
      description: 'Transportation and logistics services including trucking and intermodal solutions',
      isActive: true,
      color: 'border-green-600',
      isCarrier: true
    },
    {
      id: 'vanguard-logistics',
      title: 'VANGUARD LOGISTICS',
      logo: 'VANGUARD',
      logoStyle: 'vanguard',
      logoSubtext: 'LOGISTICS',
      description: 'Global freight forwarding and logistics solutions with comprehensive supply chain services',
      isActive: true,
      color: 'border-red-500',
      isCarrier: true
    },
    {
      id: 'shipco-broekman',
      title: 'SHIPCO / Broekman Logistics',
      logo: 'SHIPCO',
      logoStyle: 'shipco',
      description: 'NVOCC and freight consolidation services with global maritime expertise',
      isActive: true,
      color: 'border-blue-800',
      isCarrier: true
    },
    {
      id: 'ecu-worldwide',
      title: 'ECU WORLDWIDE',
      logo: 'ECU',
      logoStyle: 'ecu',
      logoSubtext: 'WORLDWIDE',
      description: 'Global LCL consolidation and freight forwarding with extensive worldwide network',
      isActive: true,
      color: 'border-blue-500',
      isCarrier: true
    }
  ];

  // Ocean Tracking carriers
  const oceanTrackingCarriers = [
    {
      id: 'hapag-lloyd',
      name: 'Hapag-Lloyd',
      logo: 'HAPAG-LLOYD',
      logoStyle: 'hapag',
      description: 'Real-time container tracking across global shipping routes',
      color: 'from-orange-500 to-orange-600',
      borderColor: 'border-orange-500',
      isActive: true
    },
    {
      id: 'msc',
      name: 'MSC',
      logo: 'MSC',
      logoStyle: 'msc',
      description: 'Mediterranean Shipping Company vessel and container tracking',
      color: 'from-blue-600 to-blue-700',
      borderColor: 'border-blue-600',
      isActive: true
    },
    {
      id: 'maersk',
      name: 'Maersk',
      logo: 'MAERSK',
      logoStyle: 'maersk',
      description: 'End-to-end shipment visibility with Maersk tracking integration',
      color: 'from-sky-500 to-sky-600',
      borderColor: 'border-sky-500',
      isActive: true
    },
    {
      id: 'cma-cgm',
      name: 'CMA CGM',
      logo: 'CMA CGM',
      logoStyle: 'cmacgm',
      description: 'French container transportation and shipping tracking services',
      color: 'from-blue-700 to-blue-800',
      borderColor: 'border-blue-700',
      isActive: true
    },
    {
      id: 'evergreen',
      name: 'Evergreen',
      logo: 'EVERGREEN',
      logoStyle: 'evergreen',
      description: 'Taiwan-based shipping line with global container tracking',
      color: 'from-green-600 to-green-700',
      borderColor: 'border-green-600',
      isActive: true
    },
    {
      id: 'cosco',
      name: 'COSCO',
      logo: 'COSCO',
      logoStyle: 'cosco',
      description: 'China Ocean Shipping Company container and vessel tracking',
      color: 'from-red-600 to-red-700',
      borderColor: 'border-red-600',
      isActive: true
    },
    {
      id: 'hmm',
      name: 'HMM',
      logo: 'HMM',
      logoStyle: 'hmm',
      description: 'Hyundai Merchant Marine shipment tracking services',
      color: 'from-orange-600 to-red-600',
      borderColor: 'border-orange-600',
      isActive: true
    },
    {
      id: 'one',
      name: 'ONE',
      logo: 'ONE',
      logoStyle: 'one',
      description: 'Ocean Network Express container tracking integration',
      color: 'from-pink-500 to-pink-600',
      borderColor: 'border-pink-500',
      isActive: true
    }
  ];

  // Terminal Tracking providers
  const terminalTrackingProviders = [
    {
      id: 'apm-terminals',
      name: 'APM Terminals',
      logo: 'APM TERMINALS',
      logoStyle: 'apm',
      description: 'Global port and terminal operations tracking with real-time container status updates',
      color: 'from-blue-600 to-cyan-600',
      borderColor: 'border-blue-600',
      isActive: true
    },
    {
      id: 'garden-city-terminal',
      name: 'Garden City Terminal',
      logo: 'GCT',
      logoStyle: 'gct',
      description: 'Georgia Ports Authority terminal in Savannah with container tracking services',
      color: 'from-green-600 to-emerald-600',
      borderColor: 'border-green-600',
      isActive: true
    },
    {
      id: 'bayport-container-terminal',
      name: 'Bayport Container Terminal',
      logo: 'BCT',
      logoStyle: 'bct',
      description: 'Port Houston container terminal with real-time tracking capabilities',
      color: 'from-blue-700 to-blue-600',
      borderColor: 'border-blue-700',
      isActive: true
    },
    {
      id: 'oakland-international',
      name: 'Oakland International Container Terminal',
      logo: 'OICT',
      logoStyle: 'oict',
      description: 'Major West Coast terminal serving Pacific trade routes',
      color: 'from-slate-700 to-slate-600',
      borderColor: 'border-slate-700',
      isActive: true
    },
    {
      id: 'seaboard-marine',
      name: 'Seaboard Marine Terminal',
      logo: 'SMT',
      logoStyle: 'smt',
      description: 'Caribbean and Latin America container terminal operations',
      color: 'from-blue-500 to-sky-500',
      borderColor: 'border-blue-500',
      isActive: true
    },
    {
      id: 'florida-international',
      name: 'Florida International Terminal',
      logo: 'FIT',
      logoStyle: 'fit',
      description: 'PortMiami container terminal with Caribbean gateway services',
      color: 'from-orange-500 to-amber-500',
      borderColor: 'border-orange-500',
      isActive: true
    },
    {
      id: 'ssa-jacksonville',
      name: 'SSA Jacksonville',
      logo: 'SSA',
      logoStyle: 'ssa',
      description: 'SSA Marine terminal operations at JAXPORT',
      color: 'from-red-600 to-red-500',
      borderColor: 'border-red-600',
      isActive: true
    },
    {
      id: 'wando-welch',
      name: 'Wando Welch Terminal',
      logo: 'WWT',
      logoStyle: 'wwt',
      description: 'SC Ports container terminal in Charleston',
      color: 'from-indigo-600 to-indigo-500',
      borderColor: 'border-indigo-600',
      isActive: true
    },
    {
      id: 'norfolk-international',
      name: 'Norfolk International Terminals',
      logo: 'NIT',
      logoStyle: 'nit',
      description: 'Port of Virginia premier container facility',
      color: 'from-purple-600 to-purple-500',
      borderColor: 'border-purple-600',
      isActive: true
    },
    {
      id: 'seagirt-marine',
      name: 'Seagirt Marine Terminal',
      logo: 'SMT',
      logoStyle: 'seagirt',
      description: 'Port of Baltimore container terminal with deep-water berths',
      color: 'from-teal-600 to-teal-500',
      borderColor: 'border-teal-600',
      isActive: true
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' }
    }
  };

  // Render logo based on style
  const renderLogo = (module) => {
    if (module.logoImage) {
      return (
        <img 
          src={module.logoImage} 
          alt={module.title}
          className="w-20 h-20 rounded-full object-cover"
        />
      );
    }

    if (module.logoStyle === 'quickbooks') {
      return (
        <div className="w-20 h-20 rounded-full bg-green-500 flex items-center justify-center">
          <span className="text-white text-2xl font-bold">qb</span>
        </div>
      );
    }

    if (module.logoStyle === 'boa') {
      return (
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center p-2">
          <span className="text-white text-xs font-semibold text-center leading-tight">Bank of America</span>
        </div>
      );
    }

    if (module.logoStyle === 'sage') {
      return (
        <div className="w-20 h-20 rounded-full bg-green-600 flex items-center justify-center">
          <span className="text-white text-xl font-bold">sage</span>
        </div>
      );
    }

    if (module.logoStyle === 'xe') {
      return (
        <div className="w-20 h-20 rounded-full bg-blue-600 flex items-center justify-center">
          <span className="text-white text-2xl font-bold">xe</span>
        </div>
      );
    }

    // Carrier Logo Styles
    if (module.logoStyle === 'pyle') {
      return (
        <div className="w-20 h-20 rounded-full bg-white flex flex-col items-center justify-center border-2 border-orange-400 shadow-sm">
          <div className="text-center">
            <span className="text-black text-xs font-medium">A.Duie</span>
            <span className="text-orange-500 text-sm font-bold block -mt-0.5">PYLE</span>
          </div>
          <span className="text-gray-500 text-[6px] mt-0.5">PYLE PEOPLE DELIVER</span>
        </div>
      );
    }

    if (module.logoStyle === 'pace') {
      return (
        <div className="w-20 h-20 rounded-full bg-white flex flex-col items-center justify-center border-2 border-amber-700 shadow-sm">
          <span className="text-amber-800 text-xl font-black tracking-tight">PACE</span>
          <span className="text-gray-500 text-[8px]">motor lines</span>
        </div>
      );
    }

    if (module.logoStyle === 'od') {
      return (
        <div className="w-20 h-20 rounded-full bg-green-700 flex flex-col items-center justify-center border-4 border-orange-500 shadow-sm">
          <span className="text-[8px] text-yellow-100 tracking-wide">OLD DOMINION</span>
          <span className="text-yellow-100 text-xl font-bold">OD</span>
          <span className="text-[8px] text-yellow-100 tracking-wide">FREIGHT LINE</span>
        </div>
      );
    }

    if (module.logoStyle === 'forwardair') {
      return (
        <div className="w-20 h-20 rounded-full bg-white flex flex-col items-center justify-center border-2 border-red-500 shadow-sm">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-red-500 rounded-full"></div>
            <div className="flex flex-col">
              <div className="h-0.5 w-3 bg-black"></div>
              <div className="h-0.5 w-3 bg-red-500 mt-0.5"></div>
            </div>
          </div>
          <span className="text-black text-[10px] font-semibold mt-1">Forward Air</span>
        </div>
      );
    }

    if (module.logoStyle === 'taxair') {
      return (
        <div className="w-20 h-20 rounded-full bg-white flex flex-col items-center justify-center border-2 border-blue-600 shadow-sm">
          <svg className="w-8 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2L4 8v8l8 6 8-6V8l-8-6zm0 2.5l5.5 4v5l-5.5 4-5.5-4v-5l5.5-4z"/>
          </svg>
          <span className="text-blue-600 text-sm font-bold mt-0.5">Tax-Air</span>
        </div>
      );
    }

    if (module.logoStyle === 'saia') {
      return (
        <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center border-2 border-red-600 shadow-sm">
          <span className="text-red-600 text-2xl font-black italic">SAIA</span>
        </div>
      );
    }

    if (module.logoStyle === 'arcbest') {
      return (
        <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center border-2 border-blue-900 shadow-sm">
          <span className="text-blue-900 text-lg font-bold">ArcBest</span>
        </div>
      );
    }

    if (module.logoStyle === 'gkt') {
      return (
        <div className="w-20 h-20 rounded-full bg-white flex flex-col items-center justify-center border-2 border-blue-700 shadow-sm">
          <div className="flex items-center">
            <span className="text-blue-900 text-xl font-black">GKT</span>
            <div className="w-2 h-2 bg-green-500 ml-0.5 -mt-2"></div>
          </div>
          <span className="text-blue-900 text-[8px] font-semibold">LOGISTICS</span>
        </div>
      );
    }

    if (module.logoStyle === 'moran') {
      return (
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-sm">
          <span className="text-white text-lg font-bold italic">MORAN</span>
        </div>
      );
    }

    if (module.logoStyle === 'vanguard') {
      return (
        <div className="w-20 h-20 rounded-lg bg-white flex flex-col items-center justify-center border-2 border-red-500 shadow-sm">
          <span className="text-red-600 text-xs font-black tracking-tight">VANGUARD</span>
          <span className="text-red-600 text-[8px] font-semibold">LOGISTICS</span>
        </div>
      );
    }

    if (module.logoStyle === 'shipco') {
      return (
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-800 to-blue-900 flex flex-col items-center justify-center shadow-sm border-4 border-blue-300">
          <div className="w-8 h-6 relative">
            <div className="absolute inset-0 border-2 border-white rounded-sm"></div>
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-2 bg-white rounded-t-sm"></div>
          </div>
          <span className="text-white text-[8px] font-bold mt-1">Broekman</span>
        </div>
      );
    }

    if (module.logoStyle === 'ecu') {
      return (
        <div className="w-20 h-20 rounded-full bg-white flex flex-col items-center justify-center border-2 border-blue-500 shadow-sm">
          <div className="flex items-center gap-1">
            <div className="w-6 h-6 relative">
              <div className="absolute top-0 left-0 w-6 h-3 bg-yellow-400 rounded-t-full"></div>
              <div className="absolute bottom-0 left-0 w-6 h-3 bg-blue-500 rounded-b-full"></div>
            </div>
            <span className="text-blue-600 text-lg font-black">ECU</span>
          </div>
          <span className="text-blue-500 text-[7px] font-semibold">WORLDWIDE</span>
        </div>
      );
    }

    // Default logo rendering
    return (
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 flex flex-col items-center justify-center border-2 border-slate-200 dark:border-slate-600">
        <span className="text-slate-700 dark:text-slate-200 text-sm font-bold text-center px-1">{module.logo}</span>
        {module.logoSubtext && (
          <span className="text-slate-500 dark:text-slate-400 text-[8px] text-center px-1">{module.logoSubtext}</span>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-cyan-50/20 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 p-6">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-[#0072FF] to-[#00C6FB] rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
            <Grid3X3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Vision Board</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Explore our comprehensive logistics solutions</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 bg-white dark:bg-slate-800 p-1.5 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 w-fit">
          <button
            onClick={() => setActiveTab('api-connectivity')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              activeTab === 'api-connectivity'
                ? 'bg-gradient-to-r from-[#0072FF] to-[#00C6FB] text-white shadow-md'
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
            }`}
          >
            <Link2 className="w-4 h-4" />
            API Connectivity
          </button>
          <button
            onClick={() => setActiveTab('tracking-connectivity')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              activeTab === 'tracking-connectivity'
                ? 'bg-gradient-to-r from-[#0072FF] to-[#00C6FB] text-white shadow-md'
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
            }`}
          >
            <Radio className="w-4 h-4" />
            Tracking Connectivity
          </button>
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        {activeTab === 'vision-board' && (
          <motion.div
            key="vision-board"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Vision Board Modules Grid */}
            <motion.div 
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
            >
              {visionModules.map((module) => (
                <motion.div
                  key={module.id}
                  variants={itemVariants}
                  onMouseEnter={() => setHoveredCard(module.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  className={`group relative overflow-hidden rounded-2xl ${module.bgColor} border border-slate-200/50 dark:border-slate-700/50 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer`}
                >
                  {/* Image Section */}
                  <div className="relative h-40 overflow-hidden">
                    {module.isLogo ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-900">
                        <div className="text-center">
                          <span className="text-3xl font-bold tracking-tight">
                            <span className="text-slate-800 dark:text-white">CARGO</span>
                            <span className="text-[#0072FF]">RATES</span>
                            <span className="text-slate-400">.ai</span>
                          </span>
                          <p className="text-xs text-slate-500 mt-1">by INFO-X</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <img 
                          src={module.image} 
                          alt={module.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                      </>
                    )}
                    
                    <div className={`absolute top-3 right-3 w-10 h-10 rounded-xl bg-gradient-to-br ${module.color} flex items-center justify-center shadow-lg transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                      <module.icon className="w-5 h-5 text-white" />
                    </div>

                    {module.hasVideo && (
                      <div className="absolute top-3 left-3 px-2 py-1 bg-red-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                        <Play className="w-3 h-3" fill="white" />
                        Video
                      </div>
                    )}

                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: hoveredCard === module.id ? 1 : 0 }}
                      className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end justify-center pb-4"
                    >
                      <button className="px-4 py-2 bg-white/90 hover:bg-white text-slate-900 text-sm font-medium rounded-lg flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                        <ExternalLink className="w-4 h-4" />
                        Open Module
                      </button>
                    </motion.div>
                  </div>

                  <div className="p-4">
                    <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-1 group-hover:text-[#0072FF] transition-colors">
                      {module.title}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2">
                      {module.description}
                    </p>
                  </div>

                  <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${module.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`} />
                </motion.div>
              ))}
            </motion.div>

            {/* Footer Stats */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4"
            >
              {[
                { label: 'Total Modules', value: visionModules.length, color: 'text-blue-600' },
                { label: 'AI-Powered', value: '5+', color: 'text-purple-600' },
                { label: 'Integrations', value: '20+', color: 'text-cyan-600' },
                { label: 'Active Users', value: '1K+', color: 'text-emerald-600' }
              ].map((stat, index) => (
                <div key={index} className="bg-white dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200/50 dark:border-slate-700/50">
                  <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        )}

        {activeTab === 'api-connectivity' && (
          <motion.div
            key="api-connectivity"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* API Connectivity Modules Grid */}
            <motion.div 
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5"
            >
              {apiModules.map((module) => (
                <motion.div
                  key={module.id}
                  variants={itemVariants}
                  className={`group relative bg-white dark:bg-slate-800 rounded-2xl border-2 ${module.color} shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden`}
                >
                  {/* Active Badge */}
                  {module.isActive && (
                    <div className="absolute top-3 right-3 z-10">
                      <CheckCircle2 className="w-6 h-6 text-green-500 fill-green-100" />
                    </div>
                  )}

                  {/* Logo Section */}
                  <div className="pt-6 pb-4 px-4 flex flex-col items-center">
                    {renderLogo(module)}
                  </div>

                  {/* Content Section */}
                  <div className="px-4 pb-5 text-center">
                    <h3 className="text-sm font-bold text-slate-800 dark:text-white mb-2 uppercase tracking-wide">
                      {module.title}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                      {module.description}
                    </p>
                  </div>

                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-600/90 to-blue-600/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <button className="px-4 py-2 bg-white text-blue-600 text-sm font-medium rounded-lg flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                      <ExternalLink className="w-4 h-4" />
                      View Details
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* API Stats */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4"
            >
              {[
                { label: 'Total Integrations', value: apiModules.length, color: 'text-blue-600' },
                { label: 'Active Connections', value: apiModules.filter(m => m.isActive).length, color: 'text-green-600' },
                { label: 'EDI Partners', value: '15+', color: 'text-purple-600' },
                { label: 'API Endpoints', value: '50+', color: 'text-cyan-600' }
              ].map((stat, index) => (
                <div key={index} className="bg-white dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200/50 dark:border-slate-700/50">
                  <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        )}

        {activeTab === 'tracking-connectivity' && (
          <motion.div
            key="tracking-connectivity"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Ocean Tracking Section */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Ship className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">Ocean Tracking</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Real-time container and vessel tracking from major shipping lines</p>
                </div>
              </div>

              <motion.div 
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
              >
                {oceanTrackingCarriers.map((carrier) => (
                  <motion.div
                    key={carrier.id}
                    variants={itemVariants}
                    className={`group relative bg-white dark:bg-slate-800 rounded-2xl border-2 ${carrier.borderColor} shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden`}
                  >
                    {/* Active Badge */}
                    {carrier.isActive && (
                      <div className="absolute top-3 right-3 z-10">
                        <CheckCircle2 className="w-6 h-6 text-green-500 fill-green-100" />
                      </div>
                    )}

                    {/* Logo Section */}
                    <div className="pt-8 pb-6 px-4 flex flex-col items-center">
                      <div className={`w-24 h-24 rounded-2xl bg-gradient-to-br ${carrier.color} flex items-center justify-center shadow-lg transform transition-transform duration-300 group-hover:scale-105`}>
                        {carrier.logoStyle === 'hapag' && (
                          <span className="text-white text-xs font-bold text-center leading-tight px-1">HAPAG-<br/>LLOYD</span>
                        )}
                        {carrier.logoStyle === 'msc' && (
                          <span className="text-white text-2xl font-black">MSC</span>
                        )}
                        {carrier.logoStyle === 'maersk' && (
                          <div className="text-center">
                            <span className="text-white text-lg font-bold">MAERSK</span>
                            <div className="w-8 h-0.5 bg-white mx-auto mt-1"></div>
                          </div>
                        )}
                        {carrier.logoStyle === 'cmacgm' && (
                          <span className="text-white text-sm font-bold text-center leading-tight">CMA<br/>CGM</span>
                        )}
                        {carrier.logoStyle === 'evergreen' && (
                          <div className="text-center">
                            <span className="text-white text-xs font-bold">EVERGREEN</span>
                            <div className="text-white text-[8px] mt-0.5">🌲</div>
                          </div>
                        )}
                        {carrier.logoStyle === 'cosco' && (
                          <span className="text-white text-lg font-bold">COSCO</span>
                        )}
                        {carrier.logoStyle === 'hmm' && (
                          <span className="text-white text-2xl font-black">HMM</span>
                        )}
                        {carrier.logoStyle === 'one' && (
                          <span className="text-white text-2xl font-black tracking-wider">ONE</span>
                        )}
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="px-4 pb-5 text-center">
                      <h3 className="text-base font-bold text-slate-800 dark:text-white mb-2">
                        {carrier.name}
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                        {carrier.description}
                      </p>
                    </div>

                    {/* Hover Overlay */}
                    <div className={`absolute inset-0 bg-gradient-to-t ${carrier.color} opacity-0 group-hover:opacity-95 transition-opacity duration-300 flex items-center justify-center`}>
                      <button className="px-4 py-2 bg-white text-slate-800 text-sm font-medium rounded-lg flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 shadow-lg">
                        <MapPin className="w-4 h-4" />
                        Track Shipment
                      </button>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Terminal Tracking Section */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl flex items-center justify-center shadow-lg">
                  <Anchor className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">Terminal Tracking</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Port and terminal operations tracking</p>
                </div>
              </div>

              <motion.div 
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5"
              >
                {terminalTrackingProviders.map((terminal) => (
                  <motion.div
                    key={terminal.id}
                    variants={itemVariants}
                    className={`group relative bg-white dark:bg-slate-800 rounded-2xl border-2 ${terminal.borderColor} shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden`}
                  >
                    {/* Active Badge */}
                    {terminal.isActive && (
                      <div className="absolute top-3 right-3 z-10">
                        <CheckCircle2 className="w-6 h-6 text-green-500 fill-green-100" />
                      </div>
                    )}

                    {/* Logo Section */}
                    <div className="pt-6 pb-4 px-4 flex flex-col items-center">
                      <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${terminal.color} flex items-center justify-center shadow-lg transform transition-transform duration-300 group-hover:scale-105`}>
                        <div className="text-center">
                          {terminal.logoStyle === 'apm' && (
                            <>
                              <span className="text-white text-lg font-bold">APM</span>
                              <span className="text-white text-[8px] font-medium block">TERMINALS</span>
                            </>
                          )}
                          {terminal.logoStyle === 'gct' && (
                            <span className="text-white text-xl font-black">GCT</span>
                          )}
                          {terminal.logoStyle === 'bct' && (
                            <span className="text-white text-xl font-black">BCT</span>
                          )}
                          {terminal.logoStyle === 'oict' && (
                            <span className="text-white text-lg font-black">OICT</span>
                          )}
                          {terminal.logoStyle === 'smt' && (
                            <span className="text-white text-xl font-black">SMT</span>
                          )}
                          {terminal.logoStyle === 'fit' && (
                            <span className="text-white text-xl font-black">FIT</span>
                          )}
                          {terminal.logoStyle === 'ssa' && (
                            <span className="text-white text-xl font-black">SSA</span>
                          )}
                          {terminal.logoStyle === 'wwt' && (
                            <span className="text-white text-xl font-black">WWT</span>
                          )}
                          {terminal.logoStyle === 'nit' && (
                            <span className="text-white text-xl font-black">NIT</span>
                          )}
                          {terminal.logoStyle === 'seagirt' && (
                            <>
                              <span className="text-white text-lg font-bold">SMT</span>
                              <span className="text-white text-[7px] font-medium block">SEAGIRT</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="px-4 pb-5 text-center">
                      <h3 className="text-base font-bold text-slate-800 dark:text-white mb-2">
                        {terminal.name}
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                        {terminal.description}
                      </p>
                    </div>

                    {/* Hover Overlay */}
                    <div className={`absolute inset-0 bg-gradient-to-t ${terminal.color} opacity-0 group-hover:opacity-95 transition-opacity duration-300 flex items-center justify-center`}>
                      <button className="px-4 py-2 bg-white text-slate-800 text-sm font-medium rounded-lg flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 shadow-lg">
                        <Container className="w-4 h-4" />
                        Track Container
                      </button>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Tracking Stats */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4"
            >
              {[
                { label: 'Ocean Carriers', value: oceanTrackingCarriers.length, color: 'text-blue-600' },
                { label: 'Terminal Partners', value: terminalTrackingProviders.length, color: 'text-slate-600' },
                { label: 'Containers Tracked', value: '50K+', color: 'text-green-600' },
                { label: 'Real-time Updates', value: '24/7', color: 'text-cyan-600' }
              ].map((stat, index) => (
                <div key={index} className="bg-white dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200/50 dark:border-slate-700/50">
                  <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VisionBoard;
